// RITA-1543 — Eine Spalte im Fenster „Vertrag erstellen“: Anschreiben oder Vertrag als Seitenansicht
// mit echten Werten, oder die Meldung statt der Vorschau (Skizze Bild 2 und 2c).
//
// Die Seiten werden wie im Vorlagen-Editor gemessen und aufgeteilt (useSeitenaufteilung) und nur für die
// Anzeige verkleinert (CSS zoom, passend zur Spaltenbreite, hooks/useEinpassZoom). Der Messbereich liegt
// deshalb AUSSERHALB der verkleinerten Box, sonst würden die gemessenen Höhen mitverkleinert und die
// Seiten anders umbrechen als gezeigt.
//
// RITA-1547 (Bild A): Rechts in der Kopfzeile der Spalte steht „Bearbeiten“, sobald es eine Vorschau gibt.
//
// RITA-1549 (Skizze 3c, Bild A, F1): Links vor dem Titel steht ein Häkchen, ob das Dokument mitgedruckt wird;
// abgewählt wird die Vorschau blasser und trägt den Hinweis „wird nicht gedruckt“. Eine Vorlage ohne
// Änderungsstand (`updated_at`) lässt sich nicht drucken: Häkchen gesperrt mit Hinweis statt stillem Auslassen.
//
// RITA-1552 (Skizze Rücklauf, Bild D; Festlegung Julia 22.10.2026): Im Fenster „Rücksendung drucken“ gibt es nur ein
// Dokument und darum kein Häkchen (Druckwahl ohne `onAuswahl`); Sperrgrund und Sperre während eines Drucks gelten
// trotzdem. Passt keine Vorlage, steht „Bearbeiten“ dort grau da, statt zu fehlen (Bild D c, `bearbeitenOhneVorschau`).
// Der Spaltentitel steht dort ohne Nummer wie in der Skizze („Anschreiben Rücksendung MUGL weiblich“); „1. …“/„2. …“
// gibt es nur mit `nummer` im Fenster „Vertrag erstellen“ (Abschlussreview MR 2, F1).
import React, { useMemo } from 'react';
import { Alert, Box, Button, Checkbox, Typography } from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import VertragSeiten from '../../templates/contractEditor/components/VertragSeiten';
import useSeitenaufteilung from '../../templates/contractEditor/layout/useSeitenaufteilung';
import { seitengeometrie } from '../../templates/contractEditor/layout/seitengeometrie';
import type { Wertemodus } from '../../templates/contractEditor/model/platzhalterwerte';
import type { Vertragsdokument } from '../../templates/contractEditor/model/types';
import useEinpassZoom from '../hooks/useEinpassZoom';
import { Meldung, vorlageUntertitel } from '../services/vertragserstellungTexte';
import { BEARBEITEN_KNOPF } from '../services/vertragsentwurfTexte';
import { WIRD_NICHT_GEDRUCKT } from '../services/vertragsdruckTexte';

interface SeitenProps {
  vorlagenname: string;
  dokument: Vertragsdokument;
  modus: Wertemodus;
}

function VorschauSeiten({ vorlagenname, dokument, modus }: SeitenProps) {
  const geometrie = useMemo(() => seitengeometrie(dokument.seite), [dokument.seite]);
  const { seiten, messung } = useSeitenaufteilung(dokument, modus, geometrie);
  const { rahmenRef, zoom } = useEinpassZoom(geometrie.breitePx);
  return (
    <>
      <Typography sx={{ fontSize: 13, color: 'text.secondary' }} data-testid="vorschau-untertitel">
        {vorlageUntertitel(vorlagenname, seiten.length)}
      </Typography>
      {messung}
      <Box sx={{ mt: 1.5, bgcolor: '#cfd6df', p: { xs: 1, sm: 2 }, borderRadius: 1, overflow: 'hidden' }}>
        <Box ref={rahmenRef}>
          <Box style={{ zoom }} data-testid="vorschau-seiten" data-zoom={zoom}>
            <VertragSeiten dokument={dokument} seiten={seiten} modus={modus} geometrie={geometrie} />
          </Box>
        </Box>
      </Box>
    </>
  );
}

function Meldungsbox({ meldung }: { meldung: Meldung }) {
  return (
    <Alert severity="warning" role="status" data-testid="vorschau-meldung" sx={{ mt: 1.5 }}>
      <Typography sx={{ fontWeight: 700, fontSize: 14 }}>{meldung.titel}</Typography>
      {meldung.vorlagen && (
        <Box component="ul" sx={{ my: 0.5, pl: 2.5 }}>
          {meldung.vorlagen.map((vorlage) => (
            <li key={vorlage}>{vorlage}</li>
          ))}
        </Box>
      )}
      {meldung.zeilen.map((zeile) => (
        <Typography key={zeile} sx={{ fontSize: 14 }}>
          {zeile}
        </Typography>
      ))}
    </Alert>
  );
}

/** RITA-1549: Häkchen „mitdrucken“ der Spalte; nur bei einer Vorschau wirksam. */
export interface Druckwahl {
  gewaehlt: boolean;
  /** Warum die Spalte nicht gedruckt werden kann (Häkchen gesperrt und abgewählt); null = druckbar. */
  sperrgrund: string | null;
  /** Ein Druck läuft: Häkchen und „Bearbeiten“ vorübergehend gesperrt. */
  gesperrt: boolean;
  /**
   * Häkchen geändert. RITA-1552: ohne Angabe kein Häkchen (nur ein Dokument, Rücksendung); Sperrgrund und Sperre
   * gelten trotzdem.
   */
  onAuswahl?: (gewaehlt: boolean) => void;
}

interface Props {
  /** Nummer vor dem Titel („1. …“); ohne Angabe nur der Titel (RITA-1552: eine Spalte ohne Häkchen, Bild D). */
  nummer?: number;
  titel: string;
  kriterien?: string;
  /** Name der gewählten Vorlage; nur zusammen mit `dokument`. */
  vorlagenname?: string;
  dokument: Vertragsdokument | null;
  modus: Wertemodus;
  meldung: Meldung | null;
  testId: string;
  /** RITA-1547: öffnet die Bearbeiten-Seite mit diesem Dokument; nur bei einer Vorschau. */
  onBearbeiten?: () => void;
  /** RITA-1549: Häkchen vor dem Titel; ohne Angabe gibt es kein Häkchen (Spalte wie bisher). */
  druck?: Druckwahl;
  /** RITA-1552 (Bild D c): „Bearbeiten“ auch ohne Vorschau zeigen – dann grau; sonst fehlt der Knopf ohne Vorschau. */
  bearbeitenOhneVorschau?: boolean;
}

function VertragVorschauSpalte({
  nummer,
  titel,
  kriterien,
  vorlagenname,
  dokument,
  modus,
  meldung,
  testId,
  onBearbeiten,
  druck,
  bearbeitenOhneVorschau = false,
}: Props) {
  const mitVorschau = !!dokument && !!vorlagenname;
  const wahl = mitVorschau && druck ? druck : null;
  const onAuswahl = wahl?.onAuswahl;
  const angehakt = !!wahl && wahl.gewaehlt && !wahl.sperrgrund;
  const abgewaehlt = !!wahl && !angehakt;
  const kopf = nummer === undefined ? titel : `${nummer}. ${titel}`;
  return (
    <Box
      data-testid={testId}
      sx={{ border: 1, borderColor: 'divider', borderRadius: 1.5, p: 1.5, bgcolor: 'background.neutral', minWidth: 0 }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
          {wahl && onAuswahl && (
            <Checkbox
              size="small"
              checked={angehakt}
              disabled={!!wahl.sperrgrund || wahl.gesperrt}
              onChange={(_, gewaehlt) => onAuswahl(gewaehlt)}
              inputProps={
                {
                  'aria-label': kopf,
                  'data-testid': `${testId}-haekchen`,
                } as React.InputHTMLAttributes<HTMLInputElement>
              }
              sx={{ p: 0.5, ml: -0.5 }}
            />
          )}
          <Typography sx={{ fontWeight: 700, minWidth: 0 }} data-testid={`${testId}-titel`}>
            {kopf}
          </Typography>
        </Box>
        {onBearbeiten && (mitVorschau || bearbeitenOhneVorschau) && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<EditOutlinedIcon />}
            onClick={onBearbeiten}
            // Während eines Drucks gesperrt (Kollegenreview !785): Ein Seitenwechsel baute das Fenster ab, bevor das
            // Ergebnis (etwa „Vorlage geändert“) die Person erreicht. RITA-1552: ohne Vorschau grau (Bild D c).
            disabled={!mitVorschau || !!wahl?.gesperrt}
            data-testid={`${testId}-bearbeiten`}
            sx={{ textTransform: 'none' }}
          >
            {BEARBEITEN_KNOPF}
          </Button>
        )}
      </Box>
      {wahl?.sperrgrund && (
        <Typography sx={{ fontSize: 13, color: 'warning.main', mt: 0.5 }} data-testid={`${testId}-nicht-druckbar`}>
          {wahl.sperrgrund}
        </Typography>
      )}
      {kriterien && (
        <Typography sx={{ fontSize: 13, color: 'text.secondary', mt: 0.5 }} data-testid={`${testId}-kriterien`}>
          {kriterien}
        </Typography>
      )}
      {abgewaehlt && !wahl?.sperrgrund && (
        <Typography
          sx={{ fontSize: 13, color: 'text.secondary', fontStyle: 'italic', mt: 0.5 }}
          data-testid={`${testId}-nicht-gedruckt`}
        >
          {WIRD_NICHT_GEDRUCKT}
        </Typography>
      )}
      <Box sx={{ opacity: abgewaehlt ? 0.45 : 1 }} data-testid={`${testId}-inhalt`}>
        {dokument && vorlagenname ? (
          <VorschauSeiten vorlagenname={vorlagenname} dokument={dokument} modus={modus} />
        ) : (
          meldung && <Meldungsbox meldung={meldung} />
        )}
      </Box>
    </Box>
  );
}

export default VertragVorschauSpalte;
